// frontend/src/state/analysis/analysisSlice.js
// Purpose: Combine initialState, reducers, extraReducers, and re-export all actions & thunks.
// Desired outcome: Keeps a single import path for all analysis actions, thunks, and selectors.

// Import necessary dependencies
import { createSlice } from '@reduxjs/toolkit';

// Import partial files from your new folder structure
import analysisInitialState from './analysisInitialState';
import analysisReducers from './analysisReducers';
import buildExtraReducers from './analysisExtraReducers';

// Import your thunks and selectors so we can re-export them under this slice.
import * as thunks from './analysisThunks';
import * as selectors from './analysisSelectors';

// 1) Create the slice by combining initialState, reducers, and extraReducers logic
const analysisSlice = createSlice({
  name: 'analysis',
  initialState: analysisInitialState,
  reducers: analysisReducers,
  extraReducers: (builder) => {
    buildExtraReducers(builder);
  }
});

// 2) Export your synchronous action creators (case reducers)
export const {
  setStep,
  updateProgress,
  clearAnalysis,
  setError,
  clearError,
  updateColumnMappings,
  updatePartMatch,
  updateConfidenceThreshold, 
  updateContextualizedPart,
  addReport,
  setCalculations,         
  setCalculationsLoading,   
  setCalculationsError,
  // Export scenario-specific calculation reducers     
  setCalculationsScenarioA,
  setCalculationsLoadingScenarioA,
  setCalculationsErrorScenarioA,
  setCalculationsScenarioB,
  setCalculationsLoadingScenarioB,
  setCalculationsErrorScenarioB,
  setCalculationsScenarioC,
  setCalculationsLoadingScenarioC,
  setCalculationsErrorScenarioC,
  updateValueSavingsScenarioType,
  updateValueSavingsAssumptions,
  resetValueSavings,
  updateReportConfig,
  updateReportName,
  setReportGenerating,
  setReportError,
  updateReportDownloadURLs,
  setReportGeneratedAt,
  setExecutiveSummary,
  updateReportRankings,
  updateCategoryAnalysis
} = analysisSlice.actions;

// 3) Export the slice's reducer as default, for use in store.js
export default analysisSlice.reducer;

// 4) Re-export all thunks
export const {
  bootstrapAnalysisData,
  fetchUploadedFiles,
  uploadFile,
  deleteFile,
  selectFile,
  fetchFileColumns,
  fetchFieldMappings,
  fetchSuggestedMappings,
  confirmColumnMappings,
  fetchConfirmedMappings,
  fetchAllCategories,
  fetchAllOemParts,
  fetchReports,
  fetchReportById,
  generateReport,
  deleteReport,
  fetchSuggestedPartMatches,
  setConfidenceThreshold,
  storeManualMatches,
  fetchConfirmedPartMatches,
  confirmPartMatches,
  fetchContextualizedParts,
  confirmContextualizedParts,
  fetchCalculations,
  calculateValueSavings,
  getValueSavingsAnalysis,
  exportReportPdf,
  exportReportExcel,
  checkCalculationReadiness,
  runCalculations,
  runCalculationsScenarioC, // Export the scenario C specific thunk
  confirmCalculations,
  getCalculationResults
} = thunks;

// 5) Re-export all selectors
export const {
  selectStep,
  selectProgress,
  selectUploadedFiles,
  selectCurrentFile,
  selectExtractedColumns,
  selectFieldMappings,
  selectColumnMappings,
  selectPartMatches,
  selectConfidenceThreshold,
  selectContextualizedParts,
  selectCalculations,
  selectCalculationsLoading,  
  selectCalculationResults,   
  selectCalculationsError,
  selectCalculationsScenarioC, // Export scenario C selectors
  selectCalculationsLoadingScenarioC,
  selectCalculationsErrorScenarioC,
  selectHasValidCalculationsScenarioC,  
  selectHasValidCalculations,
  selectReportData,
  selectReports,
  selectError,
  selectAvailableCategories,
  selectOemParts,
  selectLoadingOemParts,
  selectBootstrapInProgress,
  selectBootstrapComplete,
  selectBootstrapResults,
  selectBootstrapError,
  selectValueSavingsData,
  selectValueSavingsLoading,
  selectValueSavingsError,
  selectValueSavingsScenarioType,
  selectValueSavingsAssumptions,
  selectCalculationsReadinessCheck, 
  selectCalculationsStatus,
  selectReportConfig,
  selectReportGeneratedAt
} = selectors;